import { z } from "zod";

/// Wire-shape mirrors of the shore Kudos DTOs. Per webui T3/T4: validate at
/// the boundary with Zod; derive types via `z.infer`. Mirrors
/// `pulsewise-shore/src/modules/kudos/kudos/kudos.dto.ts` exactly, drift
/// will break the parser at runtime so it surfaces in dev/QA, not in
/// production silently.

export const KUDOS_ANALYSIS_STATUSES = ["unanalyzed", "pending", "analyzed", "failed"] as const;
export const kudosAnalysisStatusSchema = z.enum(KUDOS_ANALYSIS_STATUSES);
export type KudosAnalysisStatus = z.infer<typeof kudosAnalysisStatusSchema>;

export const KUDOS_REACTION_TYPES = ["clap"] as const;
export const kudosReactionTypeSchema = z.enum(KUDOS_REACTION_TYPES);
export type KudosReactionType = z.infer<typeof kudosReactionTypeSchema>;

const kudosUserSummarySchema = z
  .object({
    userId: z.string(),
    displayName: z.string(),
    avatarUrl: z.string().nullable(),
  })
  .strict();
export type KudosUserSummary = z.infer<typeof kudosUserSummarySchema>;

const kudosPermissionsSchema = z
  .object({
    canEdit: z.boolean(),
    canDelete: z.boolean(),
    canReact: z.boolean(),
  })
  .strict();

const kudosReactionCountsSchema = z
  .object({
    clap: z.number().int().nonnegative(),
  })
  .strict();

const kudosMyReactionsSchema = z
  .object({
    clap: z.boolean(),
  })
  .strict();

export const kudosRowSchema = z
  .object({
    id: z.string(),
    workspaceId: z.string(),
    authorUserId: z.string(),
    recipientUserIds: z.array(z.string()),
    author: kudosUserSummarySchema,
    recipients: z.array(kudosUserSummarySchema),
    message: z.string(),
    valueTags: z.array(z.string()),

    // AI-extracted slots, null in MVP / slice 1.
    title: z.string().nullable(),
    analysis: z.unknown(),
    bannerImageUrl: z.string().nullable(),
    analysisStatus: kudosAnalysisStatusSchema,
    analysisRunId: z.string().nullable(),

    commentCount: z.number().int().nonnegative(),
    reactionCounts: kudosReactionCountsSchema,
    myReactions: kudosMyReactionsSchema,

    version: z.number().int(),
    createdAt: z.string(),
    updatedAt: z.string(),
    permissions: kudosPermissionsSchema,
  })
  .strict();
export type KudosRow = z.infer<typeof kudosRowSchema>;

/// POST `/workspaces/:workspaceId/kudos` body. Strict 1..10 recipients,
/// message 25..2500, valueTags 0..10 each 1..50. Strict-mode rejects extra
/// fields (matches shore `whitelist + forbidNonWhitelisted` pipe).
export const createKudosInputSchema = z
  .object({
    recipientUserIds: z
      .array(z.string().min(1))
      .min(1, "Pick at least one recipient")
      .max(10, "Maximum 10 recipients"),
    message: z
      .string()
      .trim()
      .min(25, "Tell them what they did, at least 25 characters")
      .max(2500, "Keep it under 2,500 characters"),
    valueTags: z
      .array(z.string().trim().min(1).max(50))
      .max(10, "Maximum 10 value tags")
      .optional(),
  })
  .strict();
export type CreateKudosInput = z.infer<typeof createKudosInputSchema>;

export const listKudosQuerySchema = z
  .object({
    cursor: z.string().optional(),
    limit: z.number().int().min(1).max(100).optional(),
    valueTag: z.string().min(1).max(50).optional(),
  })
  .strict();
export type ListKudosQuery = z.infer<typeof listKudosQuerySchema>;

export const listKudosResponseSchema = z
  .object({
    rows: z.array(kudosRowSchema),
    nextCursor: z.string().nullable(),
  })
  .strict();
export type ListKudosResponse = z.infer<typeof listKudosResponseSchema>;
